import { CopyButton, Tooltip } from "@mantine/core";
import * as React from "react";
import type { ReturnOrderLineView } from "../types";
import { normalizeSerial } from "../utils/serial";

interface ReturnOrderLinesTableProps {
  lines: ReturnOrderLineView[];
  readOnly: boolean;
  /** Bấm mã hàng để tìm serial của mã đó */
  onPickItem: (itemCode: string) => void;
  /** Đổi giá trị (khác null) thì cuộn tới dòng có serial này và tô nổi một lúc */
  focusSerial?: string | null;
}

interface Totals {
  quantity: number;
  deviated: number;
}

interface CellContext {
  readOnly: boolean;
  onPickItem: (itemCode: string) => void;
}

interface Column {
  id: string;
  label: string;
  /** Bề rộng px; cột co giãn thì đây là bề rộng tối thiểu */
  width: number;
  grow?: boolean;
  numeric?: boolean;
  /** Cột chữ đứng sau cột số: lùi 14px như bảng màn xuất bán */
  indent?: boolean;
  pin?: "left" | "right";
  sortValue?: (line: ReturnOrderLineView) => string | number | null | undefined;
  cell: (line: ReturnOrderLineView, index: number, ctx: CellContext) => React.ReactNode;
  total?: (totals: Totals) => React.ReactNode;
}

type SortState = { id: string; dir: "asc" | "desc" } | null;

interface PinEdges {
  left: boolean;
  right: boolean;
}

const C = {
  surface: "#FFFFFF",
  headBg: "#F6F7F4",
  divider: "#ECEEE9",
  rowLine: "#F0F1EE",
  text: "#1B1F24",
  textSub: "#5C6570",
  muted: "#8A929B",
  empty: "#A3AAB2",
  accent: "#1F5FAE",
  accentBg: "#E6EEF8",
  highlight: "#E6EEF8",
  success: "#1E6B46",
  varianceRow: "#FFFBF3",
  danger: "#B42318",
  totalBg: "#FAFBF8",
};

const HEADER_HEIGHT = 34;
const ROW_HEIGHT = 36;
const TOTAL_HEIGHT = 40;
const EMPTY_HEIGHT = 48;
const HIGHLIGHT_MS = 1500;
const EDGE_PADDING = 14;
const PIN_SHADOW_LEFT = "6px 0 6px -6px rgba(27, 31, 36, 0.28)";
const PIN_SHADOW_RIGHT = "-6px 0 6px -6px rgba(27, 31, 36, 0.28)";

const VI_NUMBER = new Intl.NumberFormat("vi-VN");
/** Định dạng vi-VN (1.234.567); số âm dùng dấu trừ U+2212 */
const formatNumber = (value: number) => (value < 0 ? `−${VI_NUMBER.format(-value)}` : VI_NUMBER.format(value));

const toNumber = (value: unknown) => (value == null || value === "" || Number.isNaN(Number(value)) ? null : Number(value));
const deviationOf = (line: ReturnOrderLineView) => line.scannedQuantity - (Number(line.primaryQuantity) || 0);
const deviationColor = (value: number) => (value === 0 ? C.success : C.danger);

const copyButtonStyle: React.CSSProperties = {
  display: "inline-block",
  maxWidth: "100%",
  padding: 0,
  border: 0,
  background: "transparent",
  font: "inherit",
  color: "inherit",
  textAlign: "inherit",
  verticalAlign: "middle",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "copy",
};

const itemCodeCopyStyle: React.CSSProperties = { ...copyButtonStyle, color: C.accent, fontWeight: 700 };
const itemCodePickStyle: React.CSSProperties = { ...itemCodeCopyStyle, cursor: "pointer" };
const badgeStyle: React.CSSProperties = {
  flexShrink: 0,
  marginRight: 6,
  padding: "1px 6px",
  borderRadius: 5,
  background: C.accentBg,
  color: C.accent,
  fontSize: 10.5,
  fontWeight: 700,
  lineHeight: "16px",
};

const EmptyValue = () => <span style={{ color: C.empty }}>—</span>;

interface CopyTextProps {
  value: string;
  children: React.ReactNode;
  hint?: string;
  style?: React.CSSProperties;
}

/** Bấm để sao chép giá trị ô */
const CopyText = ({ value, children, hint, style }: CopyTextProps) => (
  <CopyButton value={value}>
    {({ copied, copy }) => (
      <Tooltip
        label={copied || !hint ? "Đã sao chép" : hint}
        color={copied || !hint ? C.success : undefined}
        openDelay={1000}
        opened={hint ? undefined : copied}
        multiline
        maw={360}
      >
        <button type="button" onClick={copy} style={style ? { ...copyButtonStyle, ...style } : copyButtonStyle}>
          {children}
        </button>
      </Tooltip>
    )}
  </CopyButton>
);

const textCell = (value: unknown, style?: React.CSSProperties) => {
  const text = value == null ? "" : String(value).trim();
  return text ? (
    <CopyText value={text} style={style}>
      {text}
    </CopyText>
  ) : (
    <EmptyValue />
  );
};

const numberCell = (value: unknown) => {
  const number = toNumber(value);
  return number == null ? <EmptyValue /> : <CopyText value={String(number)}>{formatNumber(number)}</CopyText>;
};


interface ItemCodeButtonProps {
  itemCode: string;
  readOnly: boolean;
  onPickItem: (itemCode: string) => void;
}

/** Mã hàng: bấm để tìm serial của mã đó rồi cuộn lên đầu trang; mã vẫn được sao chép. Chỉ xem: chỉ sao chép */
const ItemCodeButton = ({ itemCode, readOnly, onPickItem }: ItemCodeButtonProps) => (
  <CopyButton value={itemCode}>
    {({ copied, copy }) => {
      const hint = readOnly ? undefined : "Tìm serial";
      return (
        <Tooltip
          label={copied || !hint ? "Đã sao chép" : hint}
          color={copied || !hint ? C.success : undefined}
          openDelay={1000}
          opened={hint ? undefined : copied}
        >
          <button
            type="button"
            style={readOnly ? itemCodeCopyStyle : itemCodePickStyle}
            onClick={() => {
              copy();
              if (readOnly) return;
              onPickItem(itemCode);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            {itemCode}
          </button>
        </Tooltip>
      );
    }}
  </CopyButton>
);

// Thứ tự cột theo trang nhập kho cũ (columnsOrderDetail của WarehouseInWardActionV2). STT và Lệch ghim hai mép.
// Bỏ các cột với phiếu đổi trả luôn trống / luôn 0 (backend không trả cho dòng đơn đổi trả): Giá nhập, SL2,
// SL định mức, Giá theo ĐVT2, Thành tiền, Tình trạng.
const COLUMNS: Column[] = [
  {
    id: "index",
    label: "STT",
    width: 40 + EDGE_PADDING,
    pin: "left",
    cell: (_line, index) => <span style={{ color: C.muted }}>{index + 1}</span>,
  },
  {
    id: "itemCode",
    label: "Mã hàng",
    width: 120,
    sortValue: (line) => line.itemCode,
    cell: (line, _index, ctx) =>
      line.itemCode ? (
        <ItemCodeButton itemCode={line.itemCode} readOnly={ctx.readOnly} onPickItem={ctx.onPickItem} />
      ) : (
        <EmptyValue />
      ),
    total: () => <span style={{ color: C.danger }}>Tổng:</span>,
  },
  {
    id: "serial",
    label: "Serial",
    width: 134,
    sortValue: (line) => line.serial,
    cell: (line) => textCell(line.serial, { fontWeight: 500 }),
  },
  {
    id: "itemName",
    label: "Tên sản phẩm",
    width: 240,
    grow: true,
    sortValue: (line) => line.itemName,
    cell: (line) =>
      line.itemName ? (
        <span style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          {line.isBhmrPackage === true && <span style={badgeStyle}>BHMR</span>}
          <CopyText value={line.itemName} hint={line.itemName}>
            {line.itemName}
          </CopyText>
        </span>
      ) : (
        <EmptyValue />
      ),
  },
  { id: "primaryUom", label: "ĐVT1", width: 56, sortValue: (line) => line.primaryUom, cell: (line) => textCell(line.primaryUom) },
  {
    id: "primaryQuantity",
    label: "SL1",
    width: 56,
    numeric: true,
    sortValue: (line) => toNumber(line.primaryQuantity),
    cell: (line) => numberCell(line.primaryQuantity),
    total: (totals) => formatNumber(totals.quantity),
  },
  {
    id: "returnReason",
    label: "Lý do đổi trả",
    width: 140 + EDGE_PADDING,
    indent: true,
    sortValue: (line) => line.returnReason,
    cell: (line) => textCell(line.returnReason, { color: C.textSub }),
  },
  { id: "note", label: "Ghi chú", width: 200, sortValue: (line) => line.note, cell: (line) => textCell(line.note) },
  {
    id: "deviated",
    label: "Lệch",
    width: 64 + EDGE_PADDING,
    numeric: true,
    pin: "right",
    sortValue: deviationOf,
    cell: (line) => {
      const deviated = deviationOf(line);
      return <span style={{ color: deviationColor(deviated), fontWeight: 700 }}>{formatNumber(deviated)}</span>;
    },
    total: (totals) => <span style={{ color: deviationColor(totals.deviated) }}>{formatNumber(totals.deviated)}</span>,
  },
];

const GRID_TEMPLATE = COLUMNS.map((column) => (column.grow ? `minmax(${column.width}px, 1fr)` : `${column.width}px`)).join(" ");
const MIN_TABLE_WIDTH = COLUMNS.reduce((sum, column) => sum + column.width, 0);

const rowBase: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: GRID_TEMPLATE,
  alignItems: "center",
  flexShrink: 0,
};

const headerRowStyle: React.CSSProperties = {
  ...rowBase,
  height: HEADER_HEIGHT,
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: C.headBg,
  color: C.textSub,
  fontSize: 12,
  fontWeight: 600,
};

// Hàng Tổng nằm đáy khung khi ít dòng (marginTop auto) và dính đáy khi cuộn
const totalRowStyle: React.CSSProperties = {
  ...rowBase,
  height: TOTAL_HEIGHT,
  marginTop: "auto",
  position: "sticky",
  bottom: 0,
  zIndex: 2,
  borderTop: `1px solid ${C.divider}`,
  background: C.totalBg,
  fontSize: 13,
  fontWeight: 700,
};

const cellStyle = (column: Column, edges: PinEdges): React.CSSProperties => {
  const style: React.CSSProperties = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: column.numeric ? "right" : "left",
    paddingLeft: column.pin === "left" || column.indent ? EDGE_PADDING : column.numeric ? 8 : 0,
    paddingRight: column.pin === "right" ? EDGE_PADDING : column.numeric ? 0 : 8,
  };
  if (!column.pin) return style;
  let boxShadow: string | undefined;
  if (column.pin === "left" && edges.left) boxShadow = PIN_SHADOW_LEFT;
  if (column.pin === "right" && edges.right) boxShadow = PIN_SHADOW_RIGHT;
  return {
    ...style,
    position: "sticky",
    left: column.pin === "left" ? 0 : undefined,
    right: column.pin === "right" ? 0 : undefined,
    zIndex: 1,
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    justifyContent: column.numeric ? "flex-end" : "flex-start",
    background: "inherit",
    boxShadow,
  };
};

/** So sánh cho sắp xếp: số theo giá trị, chữ theo tiếng Việt; ô trống luôn nằm cuối */
const compareValues = (a: string | number | null | undefined, b: string | number | null | undefined) => {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "vi", { numeric: true, sensitivity: "base" });
};

const sortButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  maxWidth: "100%",
  padding: 0,
  border: 0,
  background: "transparent",
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
};

const SortIcon = ({ dir }: { dir: "asc" | "desc" | null }) => (
  <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
    <path d="M5 1L8.5 5h-7z" fill={dir === "asc" ? C.accent : C.empty} />
    <path d="M5 11L1.5 7h7z" fill={dir === "desc" ? C.accent : C.empty} />
  </svg>
);

const ariaSortOf = (dir: "asc" | "desc" | null) => (dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none");

interface OrderLineRowProps {
  line: ReturnOrderLineView;
  index: number;
  highlighted: boolean;
  cellStyles: React.CSSProperties[];
  ctx: CellContext;
}

/** Một dòng đơn. Memo: mỗi lần quét chỉ dòng đổi số đã quét vẽ lại */
const OrderLineRowBase = ({ line, index, highlighted, cellStyles, ctx }: OrderLineRowProps) => {
  const deviated = deviationOf(line);
  let background = C.surface;
  if (highlighted) background = C.highlight;
  else if (deviated !== 0) background = C.varianceRow;
  return (
    <div
      role="row"
      data-serial={normalizeSerial(line.serial)}
      style={{
        ...rowBase,
        height: ROW_HEIGHT,
        borderBottom: `1px solid ${C.rowLine}`,
        background,
        transition: "background-color 0.3s ease",
      }}
    >
      {COLUMNS.map((column, columnIndex) => (
        <span key={column.id} role="cell" style={cellStyles[columnIndex]}>
          {column.cell(line, index, ctx)}
        </span>
      ))}
    </div>
  );
};
const OrderLineRow = React.memo(OrderLineRowBase);

/** Dòng hàng khách trả trên đơn đổi trả, số đã quét và lệch */
const ReturnOrderLinesTable = ({ lines, readOnly, onPickItem, focusSerial = null }: ReturnOrderLinesTableProps) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState<PinEdges>({ left: false, right: false });
  const [viewWidth, setViewWidth] = React.useState(0);
  const [highlightSerial, setHighlightSerial] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortState>(null);

  // STT giữ theo thứ tự gốc của đơn; sắp xếp chỉ đổi thứ tự hiển thị
  const displayLines = React.useMemo(() => {
    const indexed = lines.map((line, index) => ({ line, index }));
    const column = sort ? COLUMNS.find((c) => c.id === sort.id) : undefined;
    if (!sort || !column?.sortValue) return indexed;
    const getValue = column.sortValue;
    const direction = sort.dir === "asc" ? 1 : -1;
    return [...indexed].sort((a, b) => {
      const result = compareValues(getValue(a.line), getValue(b.line));
      return result === 0 ? a.index - b.index : result * direction;
    });
  }, [lines, sort]);

  // Bấm lần lượt: tăng dần, giảm dần, bỏ sắp xếp
  const toggleSort = React.useCallback((id: string) => {
    setSort((prev) => {
      if (!prev || prev.id !== id) return { id, dir: "asc" };
      return prev.dir === "asc" ? { id, dir: "desc" } : null;
    });
  }, []);

  const measure = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const left = element.scrollLeft > 0;
    const right = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    setViewWidth(element.clientWidth);
  }, []);

  React.useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  React.useEffect(() => {
    const container = scrollRef.current;
    const key = normalizeSerial(focusSerial);
    if (!key || !container) return;
    const row = Array.from(container.querySelectorAll<HTMLElement>("[data-serial]")).find(
      (element) => element.dataset.serial === key,
    );
    if (!row) return;
    // Cuộn trong bảng để dòng nằm giữa hàng tiêu đề và hàng Tổng (hai hàng này dính), rồi đưa bảng vào tầm nhìn
    const top = row.offsetTop - HEADER_HEIGHT;
    const bottom = row.offsetTop + ROW_HEIGHT + TOTAL_HEIGHT - container.clientHeight;
    if (container.scrollTop > top) container.scrollTop = top;
    else if (container.scrollTop < bottom) container.scrollTop = bottom;
    container.scrollIntoView({ block: "nearest", behavior: "smooth" });
    setHighlightSerial(key);
  }, [focusSerial]);

  React.useEffect(() => {
    if (!highlightSerial) return undefined;
    const timer = window.setTimeout(() => setHighlightSerial(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlightSerial]);

  const cellStyles = React.useMemo(() => COLUMNS.map((column) => cellStyle(column, edges)), [edges]);

  const totals = React.useMemo<Totals>(
    () =>
      lines.reduce<Totals>(
        (sum, line) => ({
          quantity: sum.quantity + (Number(line.primaryQuantity) || 0),
          deviated: sum.deviated + deviationOf(line),
        }),
        { quantity: 0, deviated: 0 },
      ),
    [lines],
  );

  const ctx = React.useMemo<CellContext>(() => ({ readOnly, onPickItem }), [readOnly, onPickItem]);

  return (
    <div
      ref={scrollRef}
      onScroll={measure}
      style={{
        position: "relative",
        isolation: "isolate",
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "auto",
        background: C.surface,
        color: C.text,
        fontSize: 12.5,
        lineHeight: "normal",
      }}
    >
      <div
        role="table"
        aria-label="Dòng đơn đổi trả"
        style={{ display: "flex", flexDirection: "column", minWidth: MIN_TABLE_WIDTH, minHeight: "100%" }}
      >
        <div role="row" style={headerRowStyle}>
          {COLUMNS.map((column, columnIndex) => {
            const dir = sort?.id === column.id ? sort.dir : null;
            return (
              <span
                key={column.id}
                role="columnheader"
                aria-sort={column.sortValue ? ariaSortOf(dir) : undefined}
                style={cellStyles[columnIndex]}
              >
                {column.sortValue ? (
                  <button type="button" style={sortButtonStyle} onClick={() => toggleSort(column.id)}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{column.label}</span>
                    <SortIcon dir={dir} />
                  </button>
                ) : (
                  column.label
                )}
              </span>
            );
          })}
        </div>

        {lines.length === 0 ? (
          <div role="row" style={{ display: "flex", flexShrink: 0, height: EMPTY_HEIGHT, borderBottom: `1px solid ${C.rowLine}` }}>
            <span
              role="cell"
              aria-colspan={COLUMNS.length}
              style={{
                position: "sticky",
                left: 0,
                width: viewWidth || "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: C.textSub,
              }}
            >
              Không có dòng hàng trả
            </span>
          </div>
        ) : (
          displayLines.map(({ line, index }) => (
            <OrderLineRow
              key={`${line.sourceLineId ?? ""}-${line.serial ?? ""}-${index}`}
              line={line}
              index={index}
              highlighted={!!highlightSerial && highlightSerial === normalizeSerial(line.serial)}
              cellStyles={cellStyles}
              ctx={ctx}
            />
          ))
        )}

        <div role="row" style={totalRowStyle}>
          {COLUMNS.map((column, columnIndex) => (
            <span key={column.id} role="cell" style={cellStyles[columnIndex]}>
              {column.total?.(totals)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ReturnOrderLinesTable);
